import tenantConstants from '@constants';
import tenantPayloads from '@payloads';
import tenantTransformers from '@transformers';
import tenantUtils from '@utils';


import { getAPIBaseURL, getClassifiedBaseURL } from '../../../utility/env';
import { convertQueryObjToString } from '../../../utility/urlQuery';

const userApiEndpoints = {
  getProfileDetailsEndpoint: {
    queryFn: async (arg, api, extraOptions, baseQuery) => {
      const userId = arg?.userId || arg;
      const apiCalls = [
        baseQuery({ url: `/api/surge/users/${userId}` }),
        baseQuery({ url: `/api/surge/languages` }),
        baseQuery({ url: `/api/surge/area_units` }),
        baseQuery({ url: `/api/surge/experience_list` }),
      ];

      const results = await Promise.allSettled(apiCalls);
      const errors = results
        .map((result, index) => {
          if (result.status === 'rejected') {
            return `API call ${index + 1} failed: ${result.reason}`;
          }
          if (result.value?.error) {
            return `API call ${index + 1} returned error: ${result.value.error}`;
          }
          return null;
        })
        .filter(Boolean);

      if (errors.length > 0) {
        return { error: errors.join(', ') };
      }
      const userResponse = results[0]?.value?.data || {};
      const languagesResponse = results[1]?.value?.data;
      const areaUnitsResponse = results[2]?.value?.data;
      const experienceListResponse = results[3]?.value?.data;

      const combinedResponse = {
        ...userResponse,
        languageList: languagesResponse?.languages || [],
        areaUnits: areaUnitsResponse?.area_units || [],
        experienceList: experienceListResponse?.experience || []
      };

      const transformedData = tenantTransformers.profileDataMapper(combinedResponse?.user);
      return { data: transformedData };
    },
  },

  updateProfileDetailsEndpoint: (data) => {
    return {
      url: `/api/surge/users/${data?.userId}`,
      method: 'PUT',
      body: tenantPayloads.updateProfilePayload(data?.body),
      transformer: (response) => {  
        return response?.message || { message: 'Profile updated successfully' };
    
      },
    };
  },

  getAutoGeneratedUserDescription: {
    query: (data) =>
      `/api/surge/users/generate_description?${convertQueryObjToString(tenantPayloads.autoGenerateUserDescriptionPayload(data))}`,
  },

  getUserActiveLicenses: {
    query: ({ id, type }) => {
      return {
        url: type === 'Agency' ? `/api/surge/agencies/${id}/licenses` : `/api/surge/users/${id}/licenses`,
      };
    },
  },

  getUsersSavedSearches: {
    query: ({ locale }) => {
      return {
        url: `${getClassifiedBaseURL()}/api/user/searches/saved/`,
        method: 'GET',
        headers: {
          'Accept-Language': locale || '',
        },
      };
    },
  },

  deleteSavedSearch: {
    query: ({ locale, userID, id }) => {
      return {
        url: `${getClassifiedBaseURL()}/api/user/${userID}/searches/saved/${id}`,
        method: 'DELETE',
        headers: {
          'Accept-Language': locale || '',
        },
      };
    },
  },

  getFavouriteProperties: {
    query: () => {
      return {
        url: `${getClassifiedBaseURL()}/api/user/favorites`,
        method: 'GET',
      };
    },
  },

  saveSettings: {
    query: ({ payload }) => {
      return {
        url: `${getClassifiedBaseURL()}/api/saveSettings`,
        method: 'POST',
        body: payload,
      };
    },
  },

  updateLicense: {
    query: ({ licenseId, values }) => {
      return {
        url: `/api/surge/licenses/${licenseId}`,
        method: 'PATCH',
        body: values,
      };
    },
  },

  updateUserPreferences: (userId) => {
    return {
      url: `/api/surge/users/${userId}/toggle_auto_utilization`,
      method: 'PATCH',
    };
  },

  updatePushNotifications: ({ userId, payload }) => {
    return {
      url: `/api/surge/users/${userId}`,
      method: 'PUT',
      body: { user: payload },
    };
  },

  changePassword: {
    query: ({ userId, values, auth }) => {
      return {
        url: `/api/surge/users/${userId}/change_password`,
        method: 'POST',
        body: {
          user: {
            current_password: values?.oldPassword,
            new_password: values?.newPassword,
            new_password_confirmation: values?.confirmPassword,
          },
        },
        headers: {},
      };
    },
    transformer: (res) => {
      return res?.data || { message: 'Password Updated Successfully' };
    },
  },

  userPreference: {
    query: ({ id, values }) => {
      return {
        url: `/api/surge/users/${id}`,
        method: 'PUT',
        body: {
          user: {
            preferences: {
              email_notification: values?.email_notification,
              newsletter: values?.newsletter,
              automated_reports: values?.automated_reports,
              currency: values?.currency,
              area_unit: values?.area_unit,
            },
          },
        },
      };
    },
  },

  getUserSettingsDetail: {
    queryFn: async ({ id }, api, extraOptions, baseQuery) => {
      const apiCalls = [
        baseQuery({ url: `/api/surge/users/${id}` }),
        baseQuery({ url: `/api/surge/languages` }),
        baseQuery({ url: `/api/surge/area_units` }),
        baseQuery({ url: `/api/surge/experience_list` }),
      ];

      const results = await Promise.allSettled(apiCalls);
      const errors = results
        .map((result, index) => {
          if (result.status === 'rejected') {
            return `API call ${index + 1} failed: ${result.reason}`;
          }
          if (result.value?.error) {
            return `API call ${index + 1} returned error: ${result.value.error}`;
          }
          return null;
        })
        .filter(Boolean);

      if (errors.length > 0) {
        return { error: errors.join(', ') };
      }

      const userResponse = results[0]?.value?.data || {};
      const languagesResponse = results[1]?.value?.data;
      const areaUnitsResponse = results[2]?.value?.data;
      const experienceListResponse = results[3]?.value?.data;

      const combinedResponse = {
        ...userResponse,
        languageList: languagesResponse?.languages || [],
        areaUnits: areaUnitsResponse?.area_units || areaUnitsResponse || [],
        experienceList: experienceListResponse?.experience || experienceListResponse || []
      };

      const transformedData = tenantTransformers.settingsDetailDataMapper(combinedResponse);
      return { data: transformedData };
    },
  },

  getUserOtp: {
    query: (params) => {
      return `/api/surge/users/${params?.id}/otp?mobile_number=${params?.val}`;
    },
  },

  verifyUserOtp: {
    query: (params) => {
      return { url: `/api/surge/users/${params?.id}/verify_mobile_number`, method: 'PATCH', body: params?.values };
    },
  },

  getTruPointsCriteriaData: {
    query: () => {
      return { url: `/api/surge/users/tru_points` };
    },
  },

  getTruPointsActivityLogsData: {
    query: ({ page, userId, per_page }) => {
      const pageParam = page || 1;
      const perPageParam = per_page || 10;
      return { url: `/api/surge/users/${userId}/trubroker/tru_point_activities?page=${pageParam}&per_page=${perPageParam}` };
    },
  },
  getUserNotificationsData: {
    query: ({ page, userId }) => {
      return { url: `/api/surge/notifications?page=${page || 1}&q[user_id_eq]=${userId}` };
    },
  },

  getUserNotificationsCount: {
    query: () => {
      return { url: `/api/surge/notifications/stats` };
    },
  },

  markAsRead: {
    query: (data) => ({
      url: `/api/surge/notifications/${data?.notificationId}/mark_as_read`,
      method: 'PUT',
      body: data,
    }),
  },
  markAllAsRead: {
    query: () => ({
      url: `/api/surge/notifications/mark_all_as_read`,
      method: 'PUT',
    }),
  },

  getTruBrokerLeaderboardData: {
    query: ({ page = 1, per_page = 10 } = {}) => {
      return { url: `/api/surge/users/trubroker/leader_board?page=${page}&per_page=${per_page}` };
    },
    transformer: (response, meta, args) => {
      return tenantTransformers.truBrokerLeaderboardMapper(response);
    },
  },

  getSurgeLanguagesList: {
    query: () => ({ url: '/api/surge/languages', method: 'GET' }),
    transformer: (response) =>
      (response?.languages || []).map((e) => ({
        id: e?.id,
        name: tenantUtils.getLocalisedString(e, 'name'),
      })),
  },

  getSurgeExperienceList: {
    query: () => ({ url: '/api/surge/experience_list', method: 'GET' }),
    transformer: (response) =>
      (response?.experience || []).map((e) => ({
        id: e?.value,
        name: tenantUtils.getLocalisedString(e, 'label'),
      })),
  },

  getNafathVerificationUrl: {
    query: (params) => {
      return `${getAPIBaseURL()}/api/surge/ksa_verifications/nafaz_auth?${convertQueryObjToString(params)}`;
    },
  },

  getBankingDetails: {
    query: () => ({
      url: `/api/surge/banking_details`,
      method: 'GET',
    }),
    transformer: (response) => {
      const data = response?.data || response;
      if (!data?.banking_detail) return null;
      return {
        bankName: data.banking_detail.bank_name ?? '',
        accountTitle: data.banking_detail.account_title ?? '',
        ibanNumber: data.banking_detail.iban ?? '',
        accountNumber: data.banking_detail.account_number ?? '',
        swiftCode: data.banking_detail.swift_code ?? '',
        status: data.banking_detail.status ? { name: data.banking_detail.status.name, name_l1: data.banking_detail.status.name_l1, slug: data.banking_detail.status.slug } : null,
        rejection_reason: data.banking_detail.verification_notes,
      };
    },
  },

  saveBankDetails: {
    query: ({ body, forUpdate = false }) => {
      return {
        url: `/api/surge/banking_details`,
        method: forUpdate ? 'PUT' : 'POST',
        body,
      };
    },
    transformer: (res) => res,
  },
};

export default userApiEndpoints;
